import { codeFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  InputAdornment,
  Alert,
  Collapse,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  MenuItem,
  Snackbar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import DecimalField from '../shared/DecimalField';
import HelpButton from '../shared/HelpButton';
import { useUserStore } from '../../store';
import { DEFAULT_VAT_RATE, calcPriceExclVat } from '../../utils/vat';
import { UNIT_OPTIONS } from '../../utils/units';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';

const IDENTIFIER_TYPES = [
  { value: 'oe', labelKey: 'identifier_type_oe' },
  { value: 'aftermarket', labelKey: 'identifier_type_aftermarket' },
  { value: 'manufacturer', labelKey: 'identifier_type_manufacturer' },
  { value: 'ean', labelKey: 'identifier_type_ean' },
  { value: 'custom', labelKey: 'identifier_type_custom' },
];

const PartEditDialog = ({ open, onClose, part, onPartUpdated }) => {
  const { t } = useTranslation();
  const currentOrganization = useUserStore((state) => state.currentOrganization);

  const [formData, setFormData] = useState({
    name: '',
    unit: 'kpl',
    description: '',
    price: '',
    sell_price: '',
    manufacturer_code: '',
    manufacturer_name: '',
    is_margin_vat_scheme: false,
    margin_vat_purchase_price: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priceIncludesVat, setPriceIncludesVat] = useState(false);
  const [sellPriceIncludesVat, setSellPriceIncludesVat] = useState(false);

  // Identifier state
  const [identifiers, setIdentifiers] = useState([]);
  const [newIdCode, setNewIdCode] = useState('');
  const [newIdType, setNewIdType] = useState('oe');
  const [newIdMfr, setNewIdMfr] = useState('');
  const [idWarning, setIdWarning] = useState(null);
  const [bulkAddResult, setBulkAddResult] = useState(null);

  useEffect(() => {
    if (open && part) {
      const wasEnteredWithVat = part.price_entered_with_vat || false;
      const priceToShow = wasEnteredWithVat && part.stored_price_incl_vat
        ? part.stored_price_incl_vat
        : part.price || '';

      const sellWasEnteredWithVat = part.sell_price_entered_with_vat || false;
      const sellPriceToShow = sellWasEnteredWithVat && part.stored_sell_price_incl_vat
        ? part.stored_sell_price_incl_vat
        : part.sell_price || '';

      setFormData({
        name: part.name || '',
        unit: part.unit || 'kpl',
        description: part.description || '',
        price: priceToShow,
        sell_price: sellPriceToShow,
        manufacturer_code: part.manufacturer_code || '',
        manufacturer_name: part.manufacturer_name || '',
        is_margin_vat_scheme: part.is_margin_vat_scheme || false,
        margin_vat_purchase_price: part.margin_vat_purchase_price || '',
      });
      setPriceIncludesVat(wasEnteredWithVat);
      setSellPriceIncludesVat(sellWasEnteredWithVat);
      setIdentifiers(part.identifiers || []);
      setNewIdCode('');
      setNewIdType('oe');
      setNewIdMfr('');
      setIdWarning(null);
      setError(null);
    }
  }, [open, part]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddIdentifier = async () => {
    if (!newIdCode.trim()) return;
    try {
      const response = await axios.post(
        `/api/parts/${part.id}/identifiers/`,
        { code: newIdCode, identifier_type: newIdType, manufacturer_name: newIdMfr },
        { headers: { 'X-Organization-Id': currentOrganization?.organization_id } }
      );
      // The endpoint is idempotent — re-adding a code the part already has returns the
      // existing identifier, so dedupe by id rather than appending a second row.
      setIdentifiers((prev) =>
        prev.some((i) => i.id === response.data.id) ? prev : [...prev, response.data]
      );
      setNewIdCode('');
      setNewIdMfr('');
      if (response.data.warning) {
        setIdWarning(response.data.warning);
      }
    } catch (err) {
      setError(err.response?.data?.code?.[0] || err.response?.data?.detail || err.message);
    }
  };

  const handleBulkAddIdentifiers = async (codes) => {
    try {
      const response = await axios.post(
        `/api/parts/${part.id}/identifiers/bulk/`,
        { codes, identifier_type: newIdType, manufacturer_name: newIdMfr },
        { headers: { 'X-Organization-Id': currentOrganization?.organization_id } }
      );
      setIdentifiers((prev) => [...prev, ...response.data.created]);
      setNewIdCode('');
      setNewIdMfr('');
      setBulkAddResult({
        created: response.data.created_count,
        skipped: response.data.skipped_count,
      });
      if (response.data.warnings?.length) {
        setIdWarning(response.data.warnings.join('\n'));
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleDeleteIdentifier = async (identifierId) => {
    try {
      await axios.delete(
        `/api/parts/${part.id}/identifiers/${identifierId}/`,
        { headers: { 'X-Organization-Id': currentOrganization?.organization_id } }
      );
      setIdentifiers((prev) => prev.filter((id) => id.id !== identifierId));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleSetPrimary = async (identifierId) => {
    try {
      const response = await axios.patch(
        `/api/parts/${part.id}/identifiers/${identifierId}/`,
        { is_primary: true },
        { headers: { 'X-Organization-Id': currentOrganization?.organization_id } }
      );
      setIdentifiers((prev) =>
        prev.map((id) => ({
          ...id,
          is_primary: id.id === identifierId ? true : false,
        }))
      );
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError(t('Part name is required'));
      return;
    }

    // Validate margin VAT scheme: purchase price is required when enabled
    if (formData.is_margin_vat_scheme && !formData.margin_vat_purchase_price) {
      setError(t('Purchase price required for margin VAT scheme'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const isMargin = formData.is_margin_vat_scheme;

      // Handle purchase price VAT conversion (not used for margin VAT parts)
      const enteredPrice = !isMargin && formData.price ? parseFloat(formData.price) : null;
      let priceExclVat = enteredPrice;
      if (enteredPrice && priceIncludesVat) {
        priceExclVat = calcPriceExclVat(enteredPrice, DEFAULT_VAT_RATE);
      }

      // Handle sell price VAT conversion
      const enteredSellPrice = formData.sell_price ? parseFloat(formData.sell_price) : null;
      // For margin VAT, sell price is already gross — no conversion
      let sellPriceExclVat = enteredSellPrice;
      if (!isMargin && enteredSellPrice && sellPriceIncludesVat) {
        sellPriceExclVat = calcPriceExclVat(enteredSellPrice, DEFAULT_VAT_RATE);
      }

      // Handle margin VAT purchase price
      const marginPurchasePrice = formData.margin_vat_purchase_price
        ? parseFloat(formData.margin_vat_purchase_price)
        : null;

      const payload = {
        name: formData.name,
        unit: formData.unit || 'kpl',
        description: formData.description,
        manufacturer_code: formData.manufacturer_code,
        manufacturer_name: formData.manufacturer_name,
        price: isMargin ? null : priceExclVat,
        stored_price_incl_vat: !isMargin && priceIncludesVat ? enteredPrice : null,
        price_entered_with_vat: !isMargin && priceIncludesVat,
        // For margin VAT, sell_price is gross (customer price)
        sell_price: isMargin ? enteredSellPrice : sellPriceExclVat,
        stored_sell_price_incl_vat: isMargin
          ? (enteredSellPrice !== null ? Math.round(enteredSellPrice * 100) / 100 : null)
          : (sellPriceIncludesVat ? enteredSellPrice : null),
        sell_price_entered_with_vat: isMargin
          ? (enteredSellPrice !== null && enteredSellPrice > 0)
          : sellPriceIncludesVat,
        source_type: 'manual',
        // Margin VAT scheme fields
        is_margin_vat_scheme: isMargin,
        margin_vat_purchase_price: isMargin ? marginPurchasePrice : null,
      };

      const response = await axios.patch(`/api/parts/${part.id}/`, payload);
      if (onPartUpdated) {
        onPartUpdated(response.data);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          width: { xs: '100%', md: '600px' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
          {t('Edit Part')}
        </Typography>
        <IconButton onClick={onClose} size="small" data-testid="close-dialog-button">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              name="name"
              label={t('Part Name')}
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              name="unit"
              label={t('part_unit')}
              value={formData.unit}
              onChange={handleChange}
              select
              size="small"
              sx={{ minWidth: 110, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            >
              {UNIT_OPTIONS.map((u) => (
                <MenuItem key={u} value={u}>
                  {u} — {t('unit_' + u)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            name="description"
            label={t('Description')}
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
            <TextField
              name="manufacturer_name"
              label={t('Manufacturer')}
              value={formData.manufacturer_name}
              onChange={handleChange}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              name="manufacturer_code"
              label={t('Part Code')}
              value={formData.manufacturer_code}
              onChange={handleChange}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Box>

          {/* Identifier Codes */}
          <Divider sx={{ my: 0.5 }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
            {t('identifiers')}
          </Typography>

          {identifiers.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {identifiers.map((identifier) => (
                <Box
                  key={identifier.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: '8px',
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    sx={{
                      ...codeFont,
                      fontWeight: 500,
                      fontSize: '13px',
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {identifier.code}
                  </Typography>
                  <Chip
                    label={t(`identifier_type_${identifier.identifier_type}`)}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '11px' }}
                  />
                  {identifier.is_primary ? (
                    <StarIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => handleSetPrimary(identifier.id)}
                      title={t('identifier_primary')}
                      sx={{ p: 0.25 }}
                    >
                      <StarIcon sx={{ fontSize: 16, color: 'action.disabled' }} />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteIdentifier(identifier.id)}
                    sx={{ p: 0.25 }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Add new identifier */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              value={newIdCode}
              onChange={(e) => setNewIdCode(e.target.value)}
              onPaste={(e) => {
                const text = e.clipboardData.getData('text');
                if (/[\n\r,;\t]/.test(text)) {
                  e.preventDefault();
                  const codes = text.split(/[\n\r,;\t]+/).map(c => c.trim()).filter(Boolean);
                  if (codes.length > 1) {
                    handleBulkAddIdentifiers(codes);
                  } else if (codes.length === 1) {
                    setNewIdCode(codes[0]);
                  }
                }
              }}
              placeholder={t('identifier_code')}
              size="small"
              sx={{ flex: 1, minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              select
              value={newIdType}
              onChange={(e) => setNewIdType(e.target.value)}
              size="small"
              sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            >
              {IDENTIFIER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {t(type.labelKey)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddIdentifier}
              disabled={!newIdCode.trim()}
              startIcon={<AddIcon />}
              sx={{ borderRadius: '8px', textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {t('add_identifier')}
            </Button>
          </Box>

          <Snackbar
            open={!!idWarning}
            autoHideDuration={6000}
            onClose={() => setIdWarning(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setIdWarning(null)} severity="warning" sx={{ width: '100%' }}>
              {idWarning}
            </Alert>
          </Snackbar>
          <Snackbar
            open={!!bulkAddResult}
            autoHideDuration={6000}
            onClose={() => setBulkAddResult(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setBulkAddResult(null)} severity="success" sx={{ width: '100%' }}>
              {bulkAddResult ? t('identifiers_bulk_result', { created: bulkAddResult.created, skipped: bulkAddResult.skipped }) : ''}
            </Alert>
          </Snackbar>

          <Divider sx={{ my: 0.5 }} />

          {/* Purchase price — hidden for margin VAT (replaced by margin_vat_purchase_price) */}
          {!formData.is_margin_vat_scheme && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DecimalField
                label={priceIncludesVat ? t('Purchase price (incl. VAT)') : t('Purchase price (excl. VAT)')}
                value={formData.price}
                onChange={(value) => setFormData((prev) => ({ ...prev, price: value }))}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
              <VatModeToggle
                value={priceIncludesVat ? 'incl' : 'excl'}
                onChange={(e, val) => val && setPriceIncludesVat(val === 'incl')}
                sx={{ flexShrink: 0 }}
              />
            </Box>
            <VatConverseLine
              value={formData.price}
              mode={priceIncludesVat ? 'incl' : 'excl'}
              vatRate={DEFAULT_VAT_RATE}
            />
          </Box>
          )}

          {/* Default Sell Price — margin VAT: gross "Customer price", normal: with VAT toggle */}
          {formData.is_margin_vat_scheme ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DecimalField
              label={t('Customer price')}
              value={formData.sell_price}
              onChange={(value) => setFormData((prev) => ({ ...prev, sell_price: value }))}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontStyle: 'italic' }}>
              {t('VAT is calculated on profit margin (margin VAT scheme)')}
            </Typography>
          </Box>
          ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DecimalField
                label={sellPriceIncludesVat ? t('Default sell price (incl. VAT)') : t('Default sell price (excl. VAT)')}
                value={formData.sell_price}
                onChange={(value) => setFormData((prev) => ({ ...prev, sell_price: value }))}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
              <VatModeToggle
                value={sellPriceIncludesVat ? 'incl' : 'excl'}
                onChange={(e, val) => val && setSellPriceIncludesVat(val === 'incl')}
                sx={{ flexShrink: 0 }}
                help={false}
              />
            </Box>
            <VatConverseLine
              value={formData.sell_price}
              mode={sellPriceIncludesVat ? 'incl' : 'excl'}
              vatRate={DEFAULT_VAT_RATE}
            />
            <Typography sx={{ fontSize: '11px', color: 'text.disabled', fontStyle: 'italic' }}>
              {t('Optional. If set, this price is used instead of margin calculation.')}
            </Typography>
          </Box>
          )}

          {/* Margin VAT scheme section */}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_margin_vat_scheme}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    is_margin_vat_scheme: e.target.checked,
                    // Clear purchase price when disabled
                    margin_vat_purchase_price: e.target.checked ? prev.margin_vat_purchase_price : '',
                  }))}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      {t('Margin VAT scheme (used parts)')}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                      {t('VAT is calculated only on the profit margin, not the full price')}
                    </Typography>
                  </Box>
                  <HelpButton
                    text={t('Margin scheme help text')}
                    title={t('Margin VAT scheme')}
                    size={16}
                  />
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />

            <Collapse in={formData.is_margin_vat_scheme}>
              <Box sx={{ pl: 2, pt: 1 }}>
                <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '8px' }}>
                  {t('Margin scheme legal warning')}
                </Alert>
                <DecimalField
                  label={t('Purchase price paid')}
                  value={formData.margin_vat_purchase_price}
                  onChange={(value) => setFormData((prev) => ({ ...prev, margin_vat_purchase_price: value }))}
                  fullWidth
                  size="small"
                  required
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.5 }}>
                  {t('Enter the purchase price including VAT. VAT will be calculated on the margin.')}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ borderRadius: '10px', px: 3 }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!formData.name.trim() || loading}
          startIcon={loading ? <CircularProgress size={18} /> : null}
          sx={{ borderRadius: '10px', px: 3, bgcolor: 'primary.main' }}
        >
          {loading ? t('Saving...') : t('Save Changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartEditDialog;
